/**
 * Concept Set Service
 * Business logic for concept set CRUD operations
 */
import {
  ConceptSetListResponseSchema,
  type ConceptSet,
  type ConceptSetItem,
  type ConceptSetListItem,
} from '@/models/concept-set.types'
import {
  conceptSetItemToExpressionItem,
  mapConceptSetFromAPI,
  mapExpressionItemsFromAPI,
  type ConceptSetAPIMetadata,
  type ConceptSetAPIExpression,
  type ConceptSetAPIResponse,
} from '@/utils/api-mappers'
import { logger } from '@/utils/logger'
import { httpGet, httpPost, httpPostRead, httpPut, httpDelete } from '@/services/http-client'
import { ApiError } from '@/services/api-error'
import { getConceptsByIds } from '@/services/concept-search.service'
import { getSourceKey } from '@/config/webapi'
import { useAuthStore } from '@/stores/auth'
import { parseConceptSetObject } from '@/components/concepts/concept-set-import'

/**
 * Prefer the source key validated against the sources the server actually
 * reports; the unvalidated localStorage value is only a fallback for the
 * window before the store has loaded them.
 */
async function resolveSourceKey(): Promise<string> {
  try {
    const { useWebAPIStore } = await import('@/stores/webapi')
    return useWebAPIStore().getValidVocabularySource() || getSourceKey()
  } catch {
    return getSourceKey()
  }
}

interface ConceptSetAPIRepositoryItem {
  conceptId: number
  isExcluded: number
  includeDescendants: number
  includeMapped: number
}

// WebAPI rejects the whole expression with this 400 when any item's concept is
// absent from the requested vocabulary source.
function isMissingConceptsError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 400 &&
    typeof error.body === 'string' &&
    error.body.includes('does not contain required concepts')
  )
}

/**
 * Rebuilds the items from the stored concept ids when the vocabulary lacks some
 * of them, so the set still opens. Concepts the vocabulary can resolve keep
 * their details; the rest are flagged `missingFromVocabulary` and kept, so a
 * save does not drop them.
 */
async function getItemsTolerant(id: number | string, sourceKey: string): Promise<ConceptSetItem[]> {
  const repositoryItems = await httpGet<ConceptSetAPIRepositoryItem[]>(`/conceptset/${id}/items`)
  const conceptIds = [...new Set(repositoryItems.map(item => item.conceptId))]
  const concepts = await getConceptsByIds(sourceKey, conceptIds)
  const byId = new Map(concepts.map(concept => [concept.conceptId, concept]))

  return repositoryItems.map(item => {
    const flags = {
      isExcluded: item.isExcluded === 1,
      includeDescendants: item.includeDescendants === 1,
      includeMapped: item.includeMapped === 1,
    }
    const concept = byId.get(item.conceptId)
    if (concept) return { ...concept, ...flags }
    return {
      conceptId: item.conceptId,
      conceptName: '',
      conceptCode: '',
      domainId: '',
      vocabularyId: '',
      conceptClassId: '',
      standardConcept: null,
      invalidReason: null,
      ...flags,
      missingFromVocabulary: true,
    }
  })
}

async function getExpressionOrNull(
  id: number | string,
  sourceKey: string
): Promise<ConceptSetAPIExpression | null> {
  try {
    return await httpGet<ConceptSetAPIExpression>(`/conceptset/${id}/expression/${sourceKey}`)
  } catch (error) {
    if (isMissingConceptsError(error)) return null
    throw error
  }
}

async function fetchConceptSet(id: number | string, sourceKey: string): Promise<ConceptSet> {
  const [metadata, expression] = await Promise.all([
    httpGet<ConceptSetAPIMetadata>(`/conceptset/${id}`),
    getExpressionOrNull(id, sourceKey),
  ])

  if (expression !== null) return mapConceptSetFromAPI({ ...metadata, expression })

  logger.warn('ConceptSet', `Concept set ${id} has concepts missing from ${sourceKey}`)
  return {
    ...mapConceptSetFromAPI({ ...metadata, expression: { items: [] } }),
    items: await getItemsTolerant(id, sourceKey),
  }
}

/**
 * Get all concept sets accessible to the user
 * @returns Array of concept set list items (without full expression)
 */
export async function getAllConceptSets(): Promise<ConceptSetListItem[]> {
  try {
    const data = await httpGet<unknown>('/conceptset')
    const parsed = ConceptSetListResponseSchema.safeParse(data)

    if (!parsed.success) {
      logger.error('ConceptSet', 'Concept set list validation error', parsed.error)
      throw new Error('Invalid concept set list response')
    }

    return parsed.data
  } catch (error) {
    // A failed fetch is not "no concept sets" - propagate so the store's
    // error state renders instead of an empty list.
    logger.error('ConceptSet', 'Failed to fetch concept sets', error)
    throw error
  }
}

/**
 * Get full concept set definition including all items
 * @param id Concept set ID
 * @returns Concept set with items or null if not found
 */
export async function getConceptSetById(
  id: number | string,
  options?: { rethrow?: boolean }
): Promise<ConceptSet | null> {
  try {
    return await fetchConceptSet(id, await resolveSourceKey())
  } catch (error) {
    logger.error('ConceptSet', `Failed to fetch concept set ${id}`, error)
    // Most callers treat a failed load as "not found" (null). Callers that need
    // to tell the user *why* it failed (e.g. the Compare picker) opt into rethrow.
    if (options?.rethrow) throw error
    return null
  }
}

/**
 * Create new concept set
 * @param conceptSet Concept set data (without id)
 * @returns Created concept set with assigned ID
 */
export async function createConceptSet(
  conceptSet: Omit<ConceptSet, 'id' | 'createdDate' | 'createdBy' | 'modifiedDate' | 'modifiedBy'>
): Promise<ConceptSet> {
  try {
    const authStore = useAuthStore()
    const metadataPayload = {
      name: conceptSet.name,
      description: conceptSet.description,
    }

    const data = await authStore.executeWithUserRefresh(async () => {
      const created = await httpPost<ConceptSetAPIResponse>('/conceptset', metadataPayload)

      if ((conceptSet.items?.length || 0) > 0 && created.id) {
        const itemsPayload = (conceptSet.items || []).map(item => ({
          conceptId: item.conceptId,
          isExcluded: item.isExcluded ? 1 : 0,
          includeDescendants: item.includeDescendants ? 1 : 0,
          includeMapped: item.includeMapped ? 1 : 0,
        }))

        await httpPut(`/conceptset/${created.id}/items`, itemsPayload)
      }

      return created
    })

    if (data.id) return await fetchConceptSet(data.id, await resolveSourceKey())

    return mapConceptSetFromAPI(data)
  } catch (error) {
    logger.error('ConceptSet', 'Failed to create concept set', error)
    throw error
  }
}

/**
 * Update existing concept set
 * @param conceptSet Concept set with id
 * @returns Updated concept set
 */
export async function updateConceptSet(conceptSet: ConceptSet): Promise<ConceptSet> {
  if (!conceptSet.id) {
    throw new Error('Concept set ID is required for update')
  }

  try {
    const authStore = useAuthStore()
    const metadataPayload = {
      id: conceptSet.id,
      name: conceptSet.name,
      description: conceptSet.description,
    }

    await authStore.executeWithUserRefresh(async () => {
      await httpPut<ConceptSetAPIResponse>(`/conceptset/${conceptSet.id}`, metadataPayload)

      const itemsPayload = (conceptSet.items || []).map(item => ({
        conceptId: item.conceptId,
        isExcluded: item.isExcluded ? 1 : 0,
        includeDescendants: item.includeDescendants ? 1 : 0,
        includeMapped: item.includeMapped ? 1 : 0,
      }))

      await httpPut(`/conceptset/${conceptSet.id}/items`, itemsPayload)
    })

    return await fetchConceptSet(conceptSet.id, await resolveSourceKey())
  } catch (error) {
    logger.error('ConceptSet', `Failed to update concept set ${conceptSet.id}`, error)
    throw error
  }
}

/**
 * Delete concept set
 * @param id Concept set ID
 * @returns True if deleted successfully
 */
export async function deleteConceptSet(id: number | string): Promise<boolean> {
  try {
    await httpDelete(`/conceptset/${id}`)
    return true
  } catch (error) {
    logger.error('ConceptSet', `Failed to delete concept set ${id}`, error)
    return false
  }
}

/**
 * Assign a tag to a concept set.
 * WebAPI: POST /conceptset/{id}/tag  (body is the raw tagId int)
 */
export async function assignTagToConceptSet(
  id: number | string,
  tagId: number
): Promise<{ success: boolean; error?: string }> {
  try {
    await httpPost(`/conceptset/${id}/tag/`, tagId)
    return { success: true }
  } catch (error) {
    logger.error('ConceptSet', `Failed to assign tag ${tagId} to concept set ${id}`, error)
    return { success: false, error: error instanceof Error ? error.message : String(error) }
  }
}

/**
 * Unassign a tag from a concept set.
 * WebAPI: DELETE /conceptset/{id}/tag/{tagId}
 */
export async function unassignTagFromConceptSet(
  id: number | string,
  tagId: number
): Promise<{ success: boolean; error?: string }> {
  try {
    await httpDelete(`/conceptset/${id}/tag/${tagId}`)
    return { success: true }
  } catch (error) {
    logger.error('ConceptSet', `Failed to unassign tag ${tagId} from concept set ${id}`, error)
    return { success: false, error: error instanceof Error ? error.message : String(error) }
  }
}

/**
 * Create a concept set from an exported design. There is no server-side import
 * endpoint for concept sets, so this parses with the same parser the editor's
 * Import JSON dialog uses and then creates the set (#267).
 */
export async function importConceptSet(design: unknown, fileName: string): Promise<ConceptSet> {
  const parsed = parseConceptSetObject(design)
  if (!parsed.ok) {
    throw new Error(parsed.error ?? 'The file is not a concept set expression.')
  }

  return createConceptSet({ name: importedName(design, fileName), items: parsed.items })
}

/**
 * The design names itself where it can, matching the analysis imports, which
 * let the file decide. The file name is the next best answer, since a list
 * level import has no name field to fall back on.
 */
function importedName(design: unknown, fileName: string): string {
  const named = design as { name?: unknown }
  if (typeof named?.name === 'string' && named.name.trim()) {
    return named.name.trim()
  }
  const base = fileName.replace(/\.[^.]+$/, '').trim()
  return base || 'Imported concept set'
}

export interface ConceptSetOptimization {
  optimizedItems: ConceptSetItem[]
  removedItems: ConceptSetItem[]
}

/**
 * Asks WebAPI for the smallest expression that resolves to the same concepts
 * as `items` in the given vocabulary. The optimizer does not carry
 * `includeMapped` over; mapped concepts it resolved come back as explicit items.
 */
export async function optimizeConceptSet(
  sourceKey: string,
  items: ConceptSetItem[]
): Promise<ConceptSetOptimization> {
  const result = await httpPostRead<{
    optimizedConceptSet?: ConceptSetAPIExpression
    removedConceptSet?: ConceptSetAPIExpression
  }>(`/vocabulary/${sourceKey}/optimize`, { items: items.map(conceptSetItemToExpressionItem) })

  return {
    optimizedItems: mapExpressionItemsFromAPI(result.optimizedConceptSet),
    removedItems: mapExpressionItemsFromAPI(result.removedConceptSet),
  }
}
